import './polyfills';
import { v4 as uuidv4 } from 'uuid';
import WebFont from 'webfontloader';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { createStore, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import { applyTheme, extend } from './themes/utils';

import * as Sentry from '@sentry/react';
import { createWidgetSentry } from './utils/sentryClient';
import sentryBreadcrumbs from './middleware/sentryBreadcrumbs';

import base from './themes/base';
import seatmapRedesign from './themes/seatmapRedesign';
import { themes as BrandThemes } from './themes/index';
import { isNewSeatmapDesign } from './utils/seatmapDesign';

import reducers from './redux/reducers';

import ModalOverlay from './components/shared/ModalOverlay';
import SeatMapTrigger from './components/seatmap/SeatMapTrigger';
import BaggageWidgetTrigger from './components/v1_baggage/BaggageWidgetTrigger';
import WidgetSelector from './widgets/WidgetSelector';
import IdentityDocumentWidgetTrigger from './components/identity_document/IdentityDocumentWidgetTrigger';

import './styles/tailwind.css';
import onTrackMiddleware from './middleware/tracking';

import {
  showModal,
  setHasSeats,
  setTheme as setThemeInStore,
  setTextOverrides,
  setAcceptedProducts,
  setBaggageBasket,
  setIsModal,
  setEditMode,
  setAgentBrand,
  setLocale,
  setActiveWidget,
  hasBags,
} from './redux/actions.js';

import IntlProviderWrapper from './components/shared/IntlProviderWrapper';
import ErrorBoundary from './components/shared/ErrorBoundary';

import { speakContainerId } from './utils/sideEffects/speak';

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/includes
if (!String.prototype.includes) {
  if (!String.prototype.includes) {
    // eslint-disable-next-line no-extend-native
    String.prototype.includes = function (search, start) {
      if (search instanceof RegExp) {
        throw TypeError('first argument must not be a RegExp');
      }
      if (start === undefined) {
        start = 0;
      }
      return this.indexOf(search, start) !== -1;
    };
  }
}

const seatMapTrigger = (anchor) => {
  // eslint-disable-next-line react/no-deprecated
  ReactDOM.render(
    <Provider store={window.gordianStore}>
      <IntlProviderWrapper>
        <SeatMapTrigger />
      </IntlProviderWrapper>
    </Provider>,
    anchor
  );
};

const baggageWidgetTrigger = (anchor) => {
  // eslint-disable-next-line react/no-deprecated
  ReactDOM.render(
    <Provider store={window.gordianStore}>
      <IntlProviderWrapper>
        <BaggageWidgetTrigger />
      </IntlProviderWrapper>
    </Provider>,
    anchor
  );
};

const automaticCheckInTrigger = (anchor) => {
  // eslint-disable-next-line react/no-deprecated
  ReactDOM.render(
    <Provider store={window.gordianStore}>
      <IntlProviderWrapper>
        <IdentityDocumentWidgetTrigger />
      </IntlProviderWrapper>
    </Provider>,
    anchor
  );
};

const upsellTrigger = (anchor) => {
  // eslint-disable-next-line react/no-deprecated
  ReactDOM.render(
    <Provider store={window.gordianStore}>
      <IntlProviderWrapper>
        <BaggageWidgetTrigger />
        <SeatMapTrigger />
      </IntlProviderWrapper>
    </Provider>,
    anchor
  );
};

window.gordianInternal = (() => {
  const init = ({
    onTrack,
    sessionId,
    onBasketChange,
    disableDefaultFontLoading = false,
    disableSentry = false,
  }) => {
    if (!disableDefaultFontLoading) {
      WebFont.load({
        google: {
          families: [
            'Roboto:100,200,300,400,500,600,700,800,900',
            'Inter:100,200,300,400,500,600,700,800,900',
            'Nunito Sans:100,200,300,400,500,600,700',
          ],
        },
      });
    }
    sessionId = sessionId || uuidv4();
    const tracking = new onTrackMiddleware(onTrack);

    // Store Sentry preference globally for ErrorBoundary
    window.gordianInternal._disableSentry = disableSentry;

    // Create isolated Sentry hub for this page load (internal-only config)
    const sentry = disableSentry
      ? null
      : createWidgetSentry({
          dsn: process.env.REACT_APP_SENTRY_DSN,
          environment: process.env.REACT_APP_ENV,
          release: process.env.REACT_APP_RELEASE,
          sessionId,
          getCurrentWidget: () => {
            try {
              return (
                window.gordianStore?.getState?.().session?.activeWidget ?? null
              );
            } catch (_) {
              return null;
            }
          },
          getAgentBrand: () => {
            try {
              return (
                window.gordianStore?.getState?.().session?.agentBrand ?? null
              );
            } catch (_) {
              return null;
            }
          },
        });
    window.gordianInternal._sentry = sentry;

    // TODO: try to eliminate by passing onBasketChange into init instead of passing it into the render functions. The fact that it can be passed to the render functions suggests that different onBasketChanges can be handled, but that's not true.
    window.gordianInternal.onBasketChange = onBasketChange;

    const composeEnhancers =
      window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

    // Conditionally apply Sentry middleware only if enabled
    const middlewares = [
      thunk.withExtraArgument({ gordianInternal: window.gordianInternal }),
      tracking,
    ];

    if (!disableSentry) {
      middlewares.push(sentryBreadcrumbs);
    }

    const enhancers = composeEnhancers(
      ...middlewares.map((middleware) => applyMiddleware(middleware))
    );
    const gordianStore = createStore(reducers, {}, enhancers);

    window.gordianStore = gordianStore;

    window._gordianSessionId = sessionId;

    return Promise.resolve({ message: 'init successfully' });
  };

  const setOnBasketChange = (onBasketChange) => {
    window.gordianInternal.onBasketChange =
      onBasketChange || window.gordianInternal.onBasketChange;
    if (window.gordianInternal.onBasketChange === undefined) {
      throw new Error('you must pass onBasketChange in Gordian.init');
    }
  };

  const setUpsellTickets = (upsellTickets) => {
    // Unlike onBasketChange, upsellTickets is an optional callback, so, we only care if it is undefined post-initiation
    // i.e., if we try and upsell a seat but upsellTickets is undefined. This behavior is documented in the callbacks.js
    // tests and in actions.js.
    window.gordianInternal.upsellTickets =
      upsellTickets || window.gordianInternal.upsellTickets;
  };

  const renderSeatmap = (anchor, props) => {
    setOnBasketChange(props.onBasketChange);
    setUpsellTickets(props.upsellTickets);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.unmountComponentAtNode(anchor);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(<UpsellApp allowProducts={['seats']} {...props} />, anchor);
  };

  const renderBaggageWidget = (anchor, props) => {
    props.widgetVersions = {
      bags: 1,
    };
    setOnBasketChange(props.onBasketChange);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.unmountComponentAtNode(anchor);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(<UpsellApp allowProducts={['bags']} {...props} />, anchor);
  };

  const renderBaggageWidget2 = (anchor, props) => {
    props.widgetVersions = {
      bags: 2,
    };
    setOnBasketChange(props.onBasketChange);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.unmountComponentAtNode(anchor);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(<UpsellApp allowProducts={['bags']} {...props} />, anchor);
  };

  const renderIdentityDocumentWidget = (anchor, props) => {
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.unmountComponentAtNode(anchor);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(
      <UpsellApp allowProducts={['automatic_check_in']} {...props} />,
      anchor
    );
  };

  const showUpsell = (anchor, props) => {
    setOnBasketChange(props.onBasketChange);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.unmountComponentAtNode(anchor);
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(<UpsellApp {...props} />, anchor);
  };

  const UpsellApp = (props) => {
    const { onError = () => {} } = props;
    try {
      const { theme = {}, editMode = false, textOverrides = {} } = props;
      const {
        agentBrand = null,
        acceptedProducts = [],
        acceptedBags = {},
        segmentIndex = 0,
        onConfirmedSeats = null,
        onSeatmapLoaded = null,
        isOptIn,
        gordianUseLocalStorage = true,
        variants,
      } = props;

      let brand = agentBrand;
      window.gordianInternal.onConfirmedSeats = onConfirmedSeats;
      window.gordianInternal.onSeatmapLoaded = onSeatmapLoaded;
      window.gordianInternal.onError = onError;

      window._gordianUseLocalStorage = gordianUseLocalStorage;

      window.gordianStore.dispatch(setEditMode(editMode));
      window.gordianStore.dispatch(
        setActiveWidget(
          props.activeWidget || (props.allowProducts || ['seatmap'])[0]
        )
      );

      if (Object.keys(acceptedBags).length > 0) {
        window.gordianStore.dispatch(
          setBaggageBasket({
            bags: acceptedBags,
          })
        );
      }

      window.gordianStore.dispatch(setAgentBrand(brand));

      if (acceptedProducts.length > 0) {
        window.gordianStore.dispatch(setAcceptedProducts(acceptedProducts));
      } else if (window._gordianUseLocalStorage) {
        const localStorageAcceptedProducts = JSON.parse(
          localStorage.getItem('gordianAcceptedProducts')
        );
        if (localStorageAcceptedProducts) {
          window.gordianStore.dispatch(
            setAcceptedProducts(
              localStorageAcceptedProducts.filter(
                (product) => product.session_id === window._gordianSessionId
              )
            )
          );
        }
      }

      let brandTheme = {};
      let { fontFamily = 'Roboto' } = props;
      if (brand) {
        if (brand in BrandThemes) {
          brandTheme = BrandThemes[brand];
          if (brandTheme['fontFamily']) fontFamily = brandTheme['fontFamily'];
        }
      }
      // A partner-passed theme.fontFamily outranks the brand theme, matching
      // how every other token in the partner theme is layered on last.
      if (theme && theme['fontFamily']) fontFamily = theme['fontFamily'];

      // For the latest seatmap redesign (SEATMAP_DESIGN.NEW) layer its palette
      // on top of base before applying brand/partner overrides. Every other
      // version keeps the untouched base theme.
      const themeBase = isNewSeatmapDesign(variants)
        ? extend(base, seatmapRedesign)
        : base;

      const currentTheme = extend(extend(themeBase, brandTheme), theme);

      applyTheme(currentTheme);

      window.gordianStore.dispatch(setThemeInStore(currentTheme));

      window.gordianStore.dispatch(setTextOverrides(textOverrides));

      const { showOnLoad = true, modal = true } = props;
      const { locale = 'en-US' } = props;

      window.gordianStore.dispatch(setIsModal(modal));

      window.gordianStore.dispatch(setLocale(locale));

      window._gordianDebug = {};

      window._gordianDebug._setIsModal = (bool) =>
        window.gordianStore.dispatch(setIsModal(bool));
      window._gordianDebug._showModal = (bool) =>
        window.gordianStore.dispatch(showModal(bool));

      const speakContainer = (
        <div
          id={speakContainerId}
          style={{ height: 0, maxHeight: 0 }}
          tabIndex={-1}
        />
      );

      const Wrapper = window.gordianInternal._disableSentry
        ? ErrorBoundary
        : Sentry.ErrorBoundary;

      const wrapperProps = {
        fallback: <div>An error has occured</div>,
      };

      return (
        <Wrapper {...wrapperProps}>
          <Provider store={window.gordianStore}>
            <IntlProviderWrapper>
              {modal ? (
                <ModalOverlay
                  shown={showOnLoad}
                  modal={modal}
                  isOptIn={isOptIn}
                >
                  <WidgetSelector
                    {...props}
                    fontFamily={fontFamily}
                    segmentIndex={segmentIndex}
                    modal={modal}
                  />
                </ModalOverlay>
              ) : (
                <WidgetSelector {...props} modal={modal} />
              )}
              {speakContainer}
            </IntlProviderWrapper>
          </Provider>
        </Wrapper>
      );
    } catch (error) {
      onError(error);
      return <div></div>;
    }
  };

  const toggleModal = (option) => {
    window.gordianStore.dispatch(showModal(option));
  };

  const noSeatsOnSearch = () => {
    window.gordianStore.dispatch(setHasSeats(false));
  };

  const noBagsOnSearch = () => {
    window.gordianStore.dispatch(hasBags(false));
  };

  const noAciAvailable = () => {
    console.log('noAciAvailable');
  };

  return {
    init,
    renderSeatmap,
    renderBaggageWidget,
    renderBaggageWidget2,
    renderIdentityDocumentWidget,
    showUpsell,
    toggleModal,
    seatMapTrigger,
    baggageWidgetTrigger,
    automaticCheckInTrigger,
    upsellTrigger,
    noSeatsOnSearch,
    noBagsOnSearch,
    noAciAvailable,
  };
})();
